import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { usePageTitle } from '../utils/usePageTitle';
import './Competitors.css';

function Competitors({ websiteId }) {
  usePageTitle('Competitor Analysis');
  
  const [loading, setLoading] = useState(true);
  const [competitors, setCompetitors] = useState([]);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({ domain: '', name: '' });
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    if (websiteId) {
      loadCompetitors();
      loadOpportunities();
    }
  }, [websiteId]);

  const loadCompetitors = async () => {
    try {
      const data = await api.getCompetitors(websiteId);
      setCompetitors(data.competitors || []);
    } catch (error) {
      console.error('Load competitors error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOpportunities = async () => {
    try {
      const data = await api.getKeywordOpportunities(websiteId);
      setOpportunities(data.top_opportunities || []);
    } catch (error) {
      console.error('Load opportunities error:', error);
    }
  };

  const addCompetitor = async () => {
    if (!newCompetitor.domain.trim()) return;
    
    try {
      await api.addCompetitor(websiteId, newCompetitor.domain, newCompetitor.name);
      setShowAddModal(false);
      setNewCompetitor({ domain: '', name: '' });
      loadCompetitors();
    } catch (error) {
      alert(error.message || 'Failed to add competitor');
    }
  };

  const removeCompetitor = async (competitorId) => {
    if (!window.confirm('Remove this competitor?')) return;
    
    try {
      await api.removeCompetitor(competitorId);
      loadCompetitors();
      if (selectedCompetitor?.id === competitorId) {
        setSelectedCompetitor(null);
        setGapAnalysis(null);
      }
    } catch (error) {
      alert('Failed to remove competitor');
    }
  };

  const analyzeGap = async (competitor) => {
    setSelectedCompetitor(competitor);
    setAnalyzing(true);
    
    try {
      const data = await api.analyzeKeywordGap(websiteId, competitor.id);
      setGapAnalysis(data);
    } catch (error) {
      alert('Failed to analyze keyword gap');
    } finally {
      setAnalyzing(false);
    }
  };

  const getOpportunityColor = (score) => {
    if (score >= 70) return '#10b981'; // Green
    if (score >= 50) return '#f59e0b'; // Orange
    return '#6b7280'; // Gray
  };

  if (loading) {
    return (
      <div className="competitors-container">
        <div className="loading">Loading competitor data...</div>
      </div>
    );
  }

  return (
    <div className="competitors-container">
      {/* Header */}
      <div className="competitors-header">
        <div>
          <h1>🎯 Competitor Intelligence</h1>
          <p className="subtitle">Discover what your competitors rank for that you don't</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          + Add Competitor
        </button>
      </div>

      {/* Add Competitor Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Competitor</h2>
            <div className="form-group">
              <label>Competitor Domain</label>
              <input
                type="text"
                placeholder="example.com"
                value={newCompetitor.domain}
                onChange={(e) => setNewCompetitor({...newCompetitor, domain: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Name (Optional)</label>
              <input
                type="text"
                placeholder="Competitor Name"
                value={newCompetitor.name}
                onChange={(e) => setNewCompetitor({...newCompetitor, name: e.target.value})}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={addCompetitor}>
                Add Competitor
              </button>
            </div>
          </div>
        </div>
      )}

      {competitors.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h2>No Competitors Added</h2>
          <p>Add your competitors to discover keyword gaps and opportunities</p>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            Add Your First Competitor
          </button>
        </div>
      ) : (
        <div className="competitors-layout">
          {/* Competitor List */}
          <div className="competitors-list">
            <h2>Your Competitors ({competitors.length})</h2>
            {competitors.map((comp) => (
              <div
                key={comp.id}
                className={`competitor-card ${selectedCompetitor?.id === comp.id ? 'active' : ''}`}
                onClick={() => analyzeGap(comp)}
              >
                <div className="competitor-info">
                  <div className="competitor-name">{comp.name || comp.domain}</div>
                  <div className="competitor-domain">{comp.domain}</div>
                  <div className="competitor-stats">
                    <span className="stat">
                      <strong>{comp.overlap_keywords || 0}</strong> overlapping keywords
                    </span>
                  </div>
                </div>
                <div className="competitor-actions">
                  <button
                    className="btn-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeCompetitor(comp.id);
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Gap Analysis */}
          <div className="gap-analysis">
            {!selectedCompetitor ? (
              <div className="placeholder">
                <div className="placeholder-icon">👈</div>
                <p>Select a competitor to see keyword gap analysis</p>
              </div>
            ) : analyzing ? (
              <div className="analyzing">
                <div className="spinner"></div>
                <p>Analyzing keyword gaps...</p>
              </div>
            ) : gapAnalysis ? (
              <>
                {/* Summary Cards */}
                <div className="gap-summary">
                  <h2>Gap Analysis: {gapAnalysis.competitor.name}</h2>
                  <div className="summary-cards">
                    <div className="summary-card">
                      <div className="card-value">{gapAnalysis.summary.total_gaps}</div>
                      <div className="card-label">Keyword Gaps</div>
                      <div className="card-desc">Keywords they rank for</div>
                    </div>
                    <div className="summary-card">
                      <div className="card-value">{gapAnalysis.summary.quick_wins}</div>
                      <div className="card-label">Quick Wins</div>
                      <div className="card-desc">High opportunity score</div>
                    </div>
                    <div className="summary-card">
                      <div className="card-value">{gapAnalysis.summary.total_overlap}</div>
                      <div className="card-label">Overlap</div>
                      <div className="card-desc">Both ranking</div>
                    </div>
                    <div className="summary-card">
                      <div className="card-value">{gapAnalysis.summary.avg_opportunity_score}</div>
                      <div className="card-label">Avg Score</div>
                      <div className="card-desc">Opportunity potential</div>
                    </div>
                  </div>
                </div>

                {/* Keyword Gaps Table */}
                <div className="gaps-section">
                  <h3>🎯 Top Keyword Opportunities</h3>
                  {gapAnalysis.note && (
                    <div className="demo-notice">
                      ℹ️ {gapAnalysis.note}
                    </div>
                  )}
                  <div className="table-container">
                    <table className="gaps-table">
                      <thead>
                        <tr>
                          <th>Keyword</th>
                          <th>Their Position</th>
                          <th>Your Position</th>
                          <th>Search Volume</th>
                          <th>Intent</th>
                          <th>Opportunity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gapAnalysis.keyword_gaps.map((gap, idx) => (
                          <tr key={idx}>
                            <td className="keyword-cell">
                              <strong>{gap.keyword}</strong>
                            </td>
                            <td>
                              <span className="position-badge good">#{gap.their_position}</span>
                            </td>
                            <td>
                              <span className="position-badge poor">#{gap.your_position}</span>
                            </td>
                            <td>{gap.search_volume.toLocaleString()}</td>
                            <td>
                              <span className={`intent-badge ${gap.intent.toLowerCase()}`}>
                                {gap.intent}
                              </span>
                            </td>
                            <td>
                              <div className="opportunity-score">
                                <div className="score-bar">
                                  <div
                                    className="score-fill"
                                    style={{
                                      width: `${gap.opportunity_score}%`,
                                      background: getOpportunityColor(gap.opportunity_score)
                                    }}
                                  />
                                </div>
                                <span style={{ color: getOpportunityColor(gap.opportunity_score) }}>
                                  {gap.opportunity_score}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Competitive Overlap */}
                {gapAnalysis.competitive_overlap?.length > 0 && (
                  <div className="overlap-section">
                    <h3>⚔️ Head-to-Head Competition</h3>
                    <p className="section-desc">Keywords where you both compete in top 10</p>
                    <div className="overlap-grid">
                      {gapAnalysis.competitive_overlap.map((item, idx) => (
                        <div key={idx} className="overlap-card">
                          <div className="overlap-keyword">{item.keyword}</div>
                          <div className="overlap-positions">
                            <div>
                              <span className="label">You:</span>
                              <span className="position">#{item.your_position}</span>
                            </div>
                            <div>
                              <span className="label">Them:</span>
                              <span className="position">#{item.their_position}</span>
                            </div>
                          </div>
                          <div className="overlap-clicks">{item.your_clicks} clicks/month</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Quick Wins Section */}
      {opportunities.length > 0 && (
        <div className="quick-wins-section">
          <h2>⚡ Quick Wins Across All Competitors</h2>
          <p className="section-desc">High-opportunity keywords you can target immediately</p>
          <div className="quick-wins-grid">
            {opportunities.slice(0, 6).map((opp, idx) => (
              <div key={idx} className="quick-win-card">
                <div className="win-score" style={{ background: getOpportunityColor(opp.opportunity_score) }}>
                  {opp.opportunity_score}
                </div>
                <div className="win-keyword">{opp.keyword}</div>
                <div className="win-competitor">vs {opp.competitor}</div>
                <div className="win-stats">
                  <span>Position: #{opp.your_position}</span>
                  <span>Volume: {opp.search_volume.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Competitors;
